import { useState } from 'react';
import { useCustom } from '@refinedev/core';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface LivestreamViewerRow {
  userId: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  avatarUrl: string | null;
  totalWatchedSeconds: number;
  sessionsCount: number;
  firstJoinedAt: string;
  lastSeenAt: string;
  isActive: boolean;
  completionPercent: number;
  country: string | null;
}

interface LivestreamViewersResponse {
  total: number;
  limit: number;
  offset: number;
  streamDurationSeconds: number;
  viewers: LivestreamViewerRow[];
}

const PAGE_SIZE = 50;

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}с`;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}ч ${minutes}м`;
  }
  if (minutes >= 10) {
    return `${minutes}м`;
  }
  return `${minutes}м ${secs}с`;
}

function formatDateTime(value: string): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function fullName(row: LivestreamViewerRow): string {
  const parts = [row.firstName, row.lastName].filter(Boolean);
  if (parts.length > 0) {
    return parts.join(' ');
  }
  if (row.username) {
    return `@${row.username}`;
  }
  return `User #${row.userId}`;
}

function initials(row: LivestreamViewerRow): string {
  const f = row.firstName?.[0] ?? '';
  const l = row.lastName?.[0] ?? '';
  if (f || l) {
    return `${f}${l}`.toUpperCase();
  }
  return row.username?.slice(0, 2).toUpperCase() ?? '??';
}

export function LivestreamViewersPanel({ livestreamId }: { livestreamId: number }) {
  const [offset, setOffset] = useState(0);

  const { query } = useCustom<LivestreamViewersResponse>({
    url: `/admin/livestreams/${livestreamId}/viewers-breakdown`,
    method: 'get',
    config: {
      query: { limit: PAGE_SIZE, offset },
    },
    queryOptions: {
      refetchInterval: 30_000,
    },
  });

  const data = query.data?.data;
  const isLoading = query.isLoading;

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        Загрузка зрителей...
      </div>
    );
  }

  const viewers = data?.viewers ?? [];
  if (!data || viewers.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          По этой трансляции ещё нет зрителей.
        </CardContent>
      </Card>
    );
  }

  const limit = data.limit || PAGE_SIZE;
  const total = data.total ?? viewers.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(offset / limit) + 1;
  const hasStreamDuration = (data.streamDurationSeconds ?? 0) > 0;

  return (
    <Card>
      <CardContent className="p-0">
        <div className="px-4 py-3 flex items-center justify-between border-b">
          <div className="text-sm text-muted-foreground">
            Всего уникальных зрителей: <b className="text-foreground">{data.total}</b>
            {hasStreamDuration && (
              <>
                {' · '}
                длительность эфира:{' '}
                <b className="text-foreground">{formatDuration(data.streamDurationSeconds)}</b>
              </>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Пользователь</TableHead>
                <TableHead className="text-right">Время просмотра</TableHead>
                {hasStreamDuration && (
                  <TableHead className="text-right">% от эфира</TableHead>
                )}
                <TableHead className="text-right">Сессий</TableHead>
                <TableHead>Подключение</TableHead>
                <TableHead>Последняя активность</TableHead>
                <TableHead>Страна</TableHead>
                <TableHead>Статус</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {viewers.map((row) => (
                <TableRow key={row.userId}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        {row.avatarUrl ? (
                          <AvatarImage src={row.avatarUrl} alt={fullName(row)} />
                        ) : null}
                        <AvatarFallback className="text-xs">{initials(row)}</AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">{fullName(row)}</span>
                        {row.username && (
                          <span className="text-xs text-muted-foreground">@{row.username}</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatDuration(row.totalWatchedSeconds)}
                  </TableCell>
                  {hasStreamDuration && (
                    <TableCell className="text-right tabular-nums">
                      {row.completionPercent.toFixed(1)}%
                    </TableCell>
                  )}
                  <TableCell className="text-right tabular-nums">
                    {row.sessionsCount}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    {formatDateTime(row.firstJoinedAt)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    {formatDateTime(row.lastSeenAt)}
                  </TableCell>
                  <TableCell className="text-xs">{row.country ?? '—'}</TableCell>
                  <TableCell>
                    {row.isActive ? (
                      <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
                        в эфире
                      </Badge>
                    ) : (
                      <Badge variant="secondary">завершено</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t">
            <div className="text-xs text-muted-foreground">
              Страница {currentPage} из {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - data.limit))}
              >
                Назад
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={offset + data.limit >= data.total}
                onClick={() => setOffset(offset + data.limit)}
              >
                Вперёд
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
